import { Badge, Button, Card, CardContent, Input } from "@exhousing/design-system";
import { Plus, Search, Target } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { EmptyState } from "@/components/EmptyState";
import { LeadDrawer } from "@/components/crm/LeadDrawer";
import { useCreateLead, useCrmLookups, useLeads, useSetLeadStage } from "@/hooks/api/useCrm";
import { useUsers } from "@/hooks/api/useUsers";

function euro(v: number | null | undefined): string {
  if (v == null) return "";
  return new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(v);
}

export function PipelineBoard() {
  const { data: stages } = useCrmLookups("lead_type");
  const { data: leads } = useLeads();
  const { data: users } = useUsers();
  const setStage = useSetLeadStage();
  const createLead = useCreateLead();

  const [query, setQuery] = useState("");
  const [dragId, setDragId] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", object_city: "", price: "" });

  const stageList = stages ?? [];
  const q = query.trim().toLowerCase();
  const leadList = (leads ?? []).filter((l) => {
    if (!q) return true;
    return [l.first_name, l.last_name, l.object_city]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(q);
  });

  const ownerName = (id: string | null | undefined) => {
    if (!id) return null;
    const u = (users ?? []).find((x) => x.id === id);
    if (!u) return null;
    return [u.first_name, u.last_name].filter(Boolean).join(" ") || u.email;
  };

  const onDrop = (stageId: string) => {
    if (!dragId) return;
    const lead = leadList.find((l) => l.id === dragId);
    setDragId(null);
    if (!lead || lead.lead_type_id === stageId || lead.converted_landlord_id) return;
    setStage.mutate({ leadId: lead.id, leadTypeId: stageId });
  };

  const onCreate = (e: React.FormEvent) => {
    e.preventDefault();
    createLead.mutate(
      {
        first_name: form.first_name || null,
        last_name: form.last_name || null,
        object_city: form.object_city || null,
        price_indication: form.price ? Number(form.price) : null,
        language: "nl",
        lead_type_id: stageList[0]?.id ?? null,
      },
      {
        onSuccess: () => {
          toast.success("Lead created");
          setShowForm(false);
          setForm({ first_name: "", last_name: "", object_city: "", price: "" });
        },
        onError: (err) => toast.error(err.message),
      },
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="relative flex-1 sm:max-w-xs">
          <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search leads…"
            className="h-9 pl-8"
          />
        </div>
        <Button size="sm" onClick={() => setShowForm((s) => !s)} className="ml-auto">
          <Plus className="size-4" />
          New lead
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardContent className="pt-6">
            <form onSubmit={onCreate} className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Input placeholder="First name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
              <Input placeholder="Last name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
              <Input placeholder="Property city" value={form.object_city} onChange={(e) => setForm({ ...form, object_city: e.target.value })} />
              <Input type="number" placeholder="Price indication" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
              <div className="col-span-2 flex gap-2 sm:col-span-4">
                <Button type="submit" size="sm" disabled={createLead.isPending}>Create</Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {(leads ?? []).length === 0 ? (
        <EmptyState
          icon={Target}
          title="No acquisition leads yet"
          description="Track prospective landlords and the properties they might list. Add your first lead to start building the pipeline."
          action={{ label: "New lead", onClick: () => setShowForm(true) }}
        />
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {stageList.map((stage) => {
            const cards = leadList.filter((l) => l.lead_type_id === stage.id);
            const value = cards.reduce((s, l) => s + (l.price_indication ?? 0), 0);
            return (
              <div
                key={stage.id}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDrop(stage.id)}
                className="flex w-72 shrink-0 flex-col rounded-lg bg-muted/40 p-2"
              >
                <div className="mb-2 flex items-center gap-2 px-1">
                  <span className="size-2.5 rounded-full" style={{ background: stage.color ?? "#9b9182" }} />
                  <h3 className="text-sm font-medium">{stage.label_en}</h3>
                  <span className="ml-auto text-xs text-muted-foreground">{cards.length}</span>
                </div>
                {value > 0 && (
                  <p className="mb-2 px-1 text-xs tabular-nums text-muted-foreground">{euro(value)}</p>
                )}
                <div className="space-y-2">
                  {cards.map((lead) => {
                    const owner = ownerName(lead.owner_user_id);
                    return (
                      <Card
                        key={lead.id}
                        draggable={!lead.converted_landlord_id}
                        onDragStart={() => setDragId(lead.id)}
                        onDragEnd={() => setDragId(null)}
                        onClick={() => setSelected(lead.id)}
                        className="cursor-pointer p-3 transition-colors hover:border-primary"
                      >
                        <p className="text-sm font-medium">
                          {[lead.first_name, lead.last_name].filter(Boolean).join(" ") || "Unnamed lead"}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {lead.object_city ?? "—"} {euro(lead.price_indication)}
                        </p>
                        <div className="mt-2 flex items-center gap-2">
                          {lead.converted_landlord_id ? (
                            <Badge tone="success">Converted</Badge>
                          ) : owner ? (
                            <span className="text-xs text-muted-foreground">{owner}</span>
                          ) : null}
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <LeadDrawer leadId={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
